import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormKeys, RoofType } from '@nx-customer-apps/shared/enums';
import { MultiTextbox, TextboxQuestion } from '@nx-customer-apps/shared/utils';
import { FormToolsService } from '../../../../services/form-tools.service';
import { ValidatorsService } from '../../../../services/validators.service';
import { FormOptions, RoofDimensionsOptions } from '@nx-customer-apps/shared/interfaces';

@Injectable({
    providedIn: 'root'
})
export class RoofDimensionsFormService {
    private formToolsService = inject(FormToolsService);
    private validatorsService = inject(ValidatorsService);

    public getRoofDimensionsQuestions(options: RoofDimensionsOptions): [MultiTextbox] {
        switch (options.roofType) {
            case RoofType.SinglePitch:
                return this.getPentRoofDimensions(options);
            case RoofType.Saddle:
                return this.getGableRoofDimensions(options);
            case RoofType.Hipped:
                return this.getHippedRoofDimensions(options);
            case RoofType.HalfHipped:
                return this.getHalfHippedRoofDimensions(options);
            default:
                throw Error('"roofType" parameter must be specified');
        }
    }

    private getPentRoofDimensions(options?: FormOptions): [MultiTextbox] {
        return [
            new MultiTextbox({
                groupName: FormKeys.RoofDimensions,
                description: 'HEATLOAD.ROOF_DIMENSIONS.PENT_ROOF.DESCRIPTION',
                descriptionList: [
                    'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.DESCRIPTION',
                    'HEATLOAD.ROOF_DIMENSIONS.INCLINATION.DESCRIPTION'
                ],
                styles: 'ca-form-field-columns',
                img: 'assets/images/pent-roof-dims.png',
                questions: [
                    new TextboxQuestion({
                        key: FormKeys.HeightOfJamb,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 10 })],
                        suffix: 'MEASUREMENT_UNITS.M',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.HeightOfJamb]?.value
                    }),
                    new TextboxQuestion({
                        key: FormKeys.Inclination,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 90 })],
                        suffix: 'MEASUREMENT_UNITS.DEGREE',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.Inclination]?.value
                    })
                ]
            })
        ];
    }

    private getGableRoofDimensions(options?: FormOptions): [MultiTextbox] {
        return [
            new MultiTextbox({
                groupName: FormKeys.RoofDimensions,
                description: 'HEATLOAD.ROOF_DIMENSIONS.GABLE_ROOF.DESCRIPTION',
                descriptionList: [
                    'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.DESCRIPTION',
                    'HEATLOAD.ROOF_DIMENSIONS.INCLINATION.DESCRIPTION'
                ],
                styles: 'ca-form-field-columns',
                img: 'assets/images/gable-roof-dims.png',
                questions: [
                    new TextboxQuestion({
                        key: FormKeys.HeightOfJamb,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 10 })],
                        suffix: 'MEASUREMENT_UNITS.M',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.HeightOfJamb]?.value
                    }),
                    new TextboxQuestion({
                        key: FormKeys.Inclination,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 90 })],
                        suffix: 'MEASUREMENT_UNITS.DEGREE',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.Inclination]?.value
                    })
                ]
            })
        ];
    }

    private getHippedRoofDimensions(options?: FormOptions): [MultiTextbox] {
        return [
            new MultiTextbox({
                groupName: FormKeys.RoofDimensions,
                description: 'HEATLOAD.ROOF_DIMENSIONS.HIPPED_ROOF.DESCRIPTION',
                descriptionList: [
                    'HEATLOAD.ROOF_DIMENSIONS.INCLINATION_N1.DESCRIPTION',
                    'HEATLOAD.ROOF_DIMENSIONS.INCLINATION_N2.DESCRIPTION',
                    'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.DESCRIPTION'
                ],
                styles: 'ca-form-field-columns',
                img: 'assets/images/hipped-roof-dims.png',
                questions: [
                    new TextboxQuestion({
                        key: FormKeys.Inclination1,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION_N1.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION_N1.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 90 })],
                        suffix: 'MEASUREMENT_UNITS.DEGREE',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.Inclination1]?.value
                    }),
                    new TextboxQuestion({
                        key: FormKeys.Inclination2,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION_N2.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.INCLINATION_N2.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 90 })],
                        suffix: 'MEASUREMENT_UNITS.DEGREE',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.Inclination2]?.value
                    }),
                    new TextboxQuestion({
                        key: FormKeys.HeightOfJamb,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 10 })],
                        suffix: 'MEASUREMENT_UNITS.M',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.HeightOfJamb]?.value
                    })
                ]
            })
        ];
    }

    private getHalfHippedRoofDimensions(options?: FormOptions): [MultiTextbox] {
        return [
            new MultiTextbox({
                groupName: FormKeys.RoofDimensions,
                description: 'HEATLOAD.ROOF_DIMENSIONS.HALF_HIPPED_ROOF.DESCRIPTION',
                descriptionList: [
                    'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.DESCRIPTION',
                    'HEATLOAD.ROOF_DIMENSIONS.LENGTH.DESCRIPTION',
                    'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_H1.DESCRIPTION',
                    'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_H2.DESCRIPTION'
                ],
                styles: 'ca-form-field-columns',
                img: 'assets/images/half-hipped-roof-dims.png',
                questions: [
                    new TextboxQuestion({
                        key: FormKeys.HeightOfJamb,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_OF_JAMB.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 10 })],
                        suffix: 'MEASUREMENT_UNITS.M',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.HeightOfJamb]?.value
                    }),
                    new TextboxQuestion({
                        key: FormKeys.RoofLength,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.LENGTH.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.LENGTH.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0.1, max: 50 })],
                        suffix: 'MEASUREMENT_UNITS.M',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.RoofLength]?.value
                    }),
                    new TextboxQuestion({
                        key: FormKeys.RoofHeight1,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_H1.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_H1.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0.1, max: 10 })],
                        suffix: 'MEASUREMENT_UNITS.M',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.RoofHeight1]?.value
                    }),
                    new TextboxQuestion({
                        key: FormKeys.RoofHeight2,
                        label: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_H2.LABEL',
                        defaultErrorMessage: 'HEATLOAD.ROOF_DIMENSIONS.HEIGHT_H2.ERRORS.REQUIRED',
                        mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
                        validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0.1, max: 10 })],
                        suffix: 'MEASUREMENT_UNITS.M',
                        styles: 'with-wrapped-error-message',
                        value: options?.questions?.[FormKeys.RoofHeight2]?.value
                    })
                ]
            })
        ];
    }
}
